/**
 * useImmersiveChannelState - Shared channel navigation state for immersive pages
 * Extracts duplicate state and handlers from ImmersiveHome and ImmersiveEntityDetail
 *
 * @param {Object} options
 * @param {Function} options.handleTabClick - Tab click handler from useImmersiveTabs
 * @param {string|null} options.userEmail - Current user's email for channel ownership check
 * @param {boolean} [options.switchToContactOnSelect] - Switch to contact tab when channel selected (entity detail only)
 *
 * @returns {Object} { initialChannelId, setInitialChannelId, claimPrefill, setClaimPrefill,
 *                     handleTabChangeWithChannel, handleChannelSelected }
 *
 * @atomic @idempotent
 */
import { useState, useCallback } from 'react'
import { channelsAPI } from '../services/api'

export function useImmersiveChannelState({ handleTabClick, userEmail, switchToContactOnSelect = false }) {
  const [initialChannelId, setInitialChannelId] = useState(null)
  const [claimPrefill, setClaimPrefill] = useState(null)

  // Enhanced tab change handler supporting channel navigation and claim flows
  const handleTabChangeWithChannel = useCallback((tabId, newPanelState = null, channelId = null, tabMeta = null) => {
    if (tabMeta?.intent === 'claim_guest') {
      // Unauthenticated user: open contact tab pre-filled as a vendor claim
      setClaimPrefill({ entityId: tabMeta.entityId, entityName: tabMeta.entityName })
      handleTabClick(tabId, newPanelState)
      return
    }
    if (channelId) {
      // Set initialChannelId first, then switch to the requested tab (usually 'contact')
      setInitialChannelId(channelId)
      setTimeout(() => handleTabClick(tabId, newPanelState), 0)
    } else {
      handleTabClick(tabId, newPanelState)
    }
  }, [handleTabClick, userEmail])

  // Callback to clear initialChannelId after channel is selected
  // switchToContactOnSelect: entity detail additionally switches to the contact tab
  const handleChannelSelected = useCallback((channel) => {
    if (switchToContactOnSelect) {
      if (!channel) return
      handleTabClick('contact', 'expanded')
    }
    setTimeout(() => setInitialChannelId(null), 100)
  }, [handleTabClick, switchToContactOnSelect])

  return {
    initialChannelId,
    setInitialChannelId,
    claimPrefill,
    setClaimPrefill,
    handleTabChangeWithChannel,
    handleChannelSelected,
  }
}
